import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type { TeamMember } from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  renderJson,
  renderKeyValueBlock,
  resolveOutputMode,
} from "../../lib/output.js";

export class TeamShowCommand extends M8tCommand {
  static paths = [["team", "show"]];
  static usage = Command.Usage({
    description: "Show one team member by handle.",
    examples: [["Pretty block", "$0 team show idabest"]],
  });

  handle = Option.String();
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t-stack resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });
    const member = await apiCall<TeamMember>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "GET", path: `/api/team/${encodeURIComponent(this.handle)}` },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(member) + "\n");
      return 0;
    }

    const identityRows: { key: string; value: string | undefined }[] = [];
    if (member.identities.telegram) identityRows.push({ key: "  telegram", value: member.identities.telegram });
    if (member.identities.slack) identityRows.push({ key: "  slack", value: member.identities.slack });
    if (member.identities.teams) identityRows.push({ key: "  teams", value: member.identities.teams });

    const block = renderKeyValueBlock([
      { key: "handle", value: member.handle },
      { key: "display", value: member.displayName },
      { key: "identities", value: "" },
      ...identityRows,
      { key: "added", value: member.addedAt },
      { key: "added by", value: member.addedBy },
    ]);
    this.context.stdout.write(block + "\n");
    return 0;
  }
}
